/**
 * Header JavaScript - Simplified (no nested Opus menus)
 * When ANY nav item with submenu is hovered, ALL dropdowns open simultaneously
 */

(function () {
  "use strict";

  // Global state - shared across all dropdowns
  let dropdownOpen = false;
  let closeTimer = null;
  let scentsTimers = new Map(); // Track SCENTS timers per group

  // Debounce function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  function init() {
    const header = document.querySelector("[data-header]");
    if (!header) return;

    initMobileMenu(header);
    initDropdowns(header);
    initScentsMenus(header);
    initSearch(header);
    initKeyboardNavigation(header);
  }

  /**
   * Handle Nav Item Enter - Opens ALL dropdowns simultaneously
   * This matches React behavior where hovering ANY nav item opens ALL dropdowns
   */
  function handleNavItemEnter(event, navItem) {
    // Only trigger for nav items with submenus
    if (!navItem.hasAttribute("data-has-submenu")) {
      return;
    }

    // Clear any pending close timer
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    // Set global state - ALL dropdowns will show
    dropdownOpen = true;

    // Update wrapper attribute - CSS will show ALL dropdowns and background
    const wrapper = navItem.closest("[data-header-wrapper]");
    if (wrapper) {
      wrapper.setAttribute("data-dropdown-open", "true");

      // Calculate height for background block after dropdowns become visible
      const header = navItem.closest("[data-header]");
      if (header) {
        // px values are authored at the 16px root; scale them with the root font size
        const rem = (px) =>
          (px * parseFloat(getComputedStyle(document.documentElement).fontSize)) / 16;
        const calculateBackgroundHeight = () => {
          const allDropdowns = header.querySelectorAll("[data-dropdown]");
          let maxHeight = rem(180); // Default minimum height

          allDropdowns.forEach((dropdown) => {
            // Temporarily make visible to measure
            const wasVisible = dropdown.style.visibility !== "hidden";
            if (!wasVisible) {
              dropdown.style.visibility = "visible";
              dropdown.style.opacity = "1";
            }

            const height = dropdown.offsetHeight;
            if (height > 0) {
              maxHeight = Math.max(maxHeight, height + rem(40)); // Add padding
            }

            if (!wasVisible) {
              dropdown.style.visibility = "";
              dropdown.style.opacity = "";
            }
          });

          const background = wrapper.querySelector("[data-dropdown-background]");
          if (background) {
            background.style.maxHeight = maxHeight + "px";
            background.style.minHeight = maxHeight + "px"; // Set min-height to match calculated height
          }
        };

        // Calculate after a brief delay to ensure dropdowns are rendered
        requestAnimationFrame(() => {
          calculateBackgroundHeight();
        });
      }
    }

    // Close search if open
    const header = navItem.closest("[data-header]");
    if (header) {
      const searchContainer = header.querySelector("[data-search-container]");
      const searchToggle = header.querySelector("[data-search-toggle]");
      if (searchContainer && searchContainer.getAttribute("aria-hidden") === "false") {
        searchContainer.setAttribute("aria-hidden", "true");
        if (searchToggle) {
          searchToggle.setAttribute("aria-expanded", "false");
        }
      }
    }
  }

  /**
   * Handle Header Leave - Closes ALL dropdowns after delay
   */
  function handleHeaderLeave(event) {
    const wrapper =
      event.currentTarget ||
      event.target.closest("[data-header-wrapper]") ||
      document.querySelector("[data-header-wrapper]");
    if (!wrapper) return;

    // Don't close if moving to a dropdown or another nav item
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && wrapper.contains(relatedTarget)) {
      return;
    }

    // Clear any existing timer
    if (closeTimer) {
      clearTimeout(closeTimer);
    }

    // Close after 600ms delay (matching React)
    closeTimer = setTimeout(() => {
      dropdownOpen = false;
      wrapper.setAttribute("data-dropdown-open", "false");
      closeTimer = null;
    }, 600);
  }

  /**
   * Initialize SCENTS Nested Menus - Hover over "SCENTS" reveals Opus items
   */
  function initScentsMenus(header) {
    const wrapper = header.querySelector("[data-header-wrapper]");
    if (!wrapper) return;

    // Use event delegation on wrapper to handle SCENTS groups
    wrapper.addEventListener(
      "mouseenter",
      function (e) {
        const scentsGroup = e.target.closest("[data-scents-group]");
        if (scentsGroup) {
          handleScentsEnter(e, scentsGroup);
        }
      },
      true,
    );

    wrapper.addEventListener(
      "mouseleave",
      function (e) {
        const scentsGroup = e.target.closest("[data-scents-group]");
        if (scentsGroup) {
          handleScentsLeave(e, scentsGroup);
        }
      },
      true,
    );

    // Initialize all SCENTS groups as closed
    const scentsGroups = header.querySelectorAll("[data-scents-group]");
    scentsGroups.forEach((group) => {
      group.setAttribute("data-scents-open", "false");
    });
  }

  /**
   * Handle SCENTS Enter - Shows Opus submenu
   */
  function handleScentsEnter(event, scentsGroup) {
    // Clear any pending timer for this group
    const timerId = scentsGroup.getAttribute("data-scents-id") || String(Date.now());
    scentsGroup.setAttribute("data-scents-id", timerId);

    if (scentsTimers.has(timerId)) {
      clearTimeout(scentsTimers.get(timerId));
      scentsTimers.delete(timerId);
    }

    // Show SCENTS menu immediately
    scentsGroup.setAttribute("data-scents-open", "true");
  }

  /**
   * Handle SCENTS Leave - Hides Opus submenu after delay
   */
  function handleScentsLeave(event, scentsGroup) {
    const timerId = scentsGroup.getAttribute("data-scents-id");
    if (!timerId) return;

    // Clear existing timer
    if (scentsTimers.has(timerId)) {
      clearTimeout(scentsTimers.get(timerId));
    }

    // Hide after 400ms delay (matching React behavior)
    const timer = setTimeout(() => {
      scentsGroup.setAttribute("data-scents-open", "false");
      scentsTimers.delete(timerId);
    }, 400);

    scentsTimers.set(timerId, timer);
  }

  /**
   * Mobile Menu Toggle
   */
  function initMobileMenu(header) {
    const toggle = header.querySelector("[data-mobile-menu-toggle]");
    const menu = header.querySelector("[data-mobile-menu]");

    if (!toggle || !menu) return;

    let mobileOpenIndex = null; // Track which submenu is open

    toggle.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();

      const isOpen = toggle.getAttribute("aria-expanded") === "true";
      const willBeOpen = !isOpen;

      toggle.setAttribute("aria-expanded", willBeOpen);
      menu.setAttribute("aria-hidden", !willBeOpen);

      // Focus mobile search input when opening
      if (willBeOpen) {
        const mobileSearchInput = menu.querySelector("[data-mobile-search-input]");
        if (mobileSearchInput) {
          requestAnimationFrame(() => {
            mobileSearchInput.focus();
          });
        }
      }

      // Close search if open
      const searchContainer = header.querySelector("[data-search-container]");
      if (searchContainer && searchContainer.getAttribute("aria-hidden") === "false") {
        searchContainer.setAttribute("aria-hidden", "true");
        const searchToggle = header.querySelector("[data-search-toggle]");
        if (searchToggle) {
          searchToggle.setAttribute("aria-expanded", "false");
        }
      }

      // Prevent body scroll when menu is open (mobile/tablet only)
      if (willBeOpen && window.innerWidth < 1024) {
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
      } else {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
        // Close all submenus when closing main menu
        mobileOpenIndex = null;
        const allNavItems = menu.querySelectorAll("[data-mobile-nav-item]");
        allNavItems.forEach((item) => {
          item.removeAttribute("data-mobile-expanded");
          const link = item.querySelector("[data-mobile-nav-link]");
          if (link) {
            link.removeAttribute("aria-expanded");
            link.removeAttribute("aria-controls");
          }
        });
      }
    });

    // Handle expandable/collapsible submenus
    const navItems = menu.querySelectorAll("[data-mobile-nav-item]");
    navItems.forEach((navItem, index) => {
      const link = navItem.querySelector("[data-mobile-nav-link][data-has-submenu]");
      if (!link) return;

      const submenu = navItem.querySelector("[data-mobile-submenu]");
      if (!submenu) return;

      link.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();

        const isExpanded = mobileOpenIndex === index;

        // Close all other submenus
        navItems.forEach((item, idx) => {
          if (idx !== index) {
            item.removeAttribute("data-mobile-expanded");
            const otherLink = item.querySelector("[data-mobile-nav-link]");
            if (otherLink) {
              otherLink.removeAttribute("aria-expanded");
            }
          }
        });

        // Toggle current submenu
        if (isExpanded) {
          mobileOpenIndex = null;
          navItem.removeAttribute("data-mobile-expanded");
          link.removeAttribute("aria-expanded");
        } else {
          mobileOpenIndex = index;
          navItem.setAttribute("data-mobile-expanded", "true");
          link.setAttribute("aria-expanded", "true");
          link.setAttribute("aria-controls", submenu.id || `mobile-submenu-${index}`);
        }
      });
    });

    // Close menu when clicking submenu links
    const submenuLinks = menu.querySelectorAll("[data-mobile-submenu] a");
    submenuLinks.forEach((link) => {
      link.addEventListener("click", function () {
        toggle.setAttribute("aria-expanded", "false");
        menu.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
        mobileOpenIndex = null;
        // Close all submenus
        navItems.forEach((item) => {
          item.removeAttribute("data-mobile-expanded");
          const itemLink = item.querySelector("[data-mobile-nav-link]");
          if (itemLink) {
            itemLink.removeAttribute("aria-expanded");
          }
        });
      });
    });

    // Close menu when clicking outside (mobile/tablet only)
    document.addEventListener("click", function (e) {
      if (window.innerWidth < 1024) {
        if (!menu.contains(e.target) && !toggle.contains(e.target)) {
          if (toggle.getAttribute("aria-expanded") === "true") {
            toggle.setAttribute("aria-expanded", "false");
            menu.setAttribute("aria-hidden", "true");
            document.body.style.overflow = "";
            document.body.style.position = "";
            document.body.style.width = "";
            mobileOpenIndex = null;
            // Close all submenus
            navItems.forEach((item) => {
              item.removeAttribute("data-mobile-expanded");
              const link = item.querySelector("[data-mobile-nav-link]");
              if (link) {
                link.removeAttribute("aria-expanded");
              }
            });
          }
        }
      }
    });

    // Close menu on navigation link click (non-submenu links)
    const directLinks = menu.querySelectorAll("[data-mobile-nav-link]:not([data-has-submenu])");
    directLinks.forEach((link) => {
      link.addEventListener("click", function () {
        toggle.setAttribute("aria-expanded", "false");
        menu.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
        mobileOpenIndex = null;
      });
    });

    // Close menu on ESC key
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") {
        toggle.setAttribute("aria-expanded", "false");
        menu.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
        mobileOpenIndex = null;
        // Close all submenus
        navItems.forEach((item) => {
          item.removeAttribute("data-mobile-expanded");
          const link = item.querySelector("[data-mobile-nav-link]");
          if (link) {
            link.removeAttribute("aria-expanded");
          }
        });
        toggle.focus();
      }
    });

    // Handle window resize - close menu if switching to desktop
    window.addEventListener("resize", function () {
      if (window.innerWidth >= 1024 && toggle.getAttribute("aria-expanded") === "true") {
        toggle.setAttribute("aria-expanded", "false");
        menu.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
        mobileOpenIndex = null;
        // Close all submenus
        navItems.forEach((item) => {
          item.removeAttribute("data-mobile-expanded");
          const link = item.querySelector("[data-mobile-nav-link]");
          if (link) {
            link.removeAttribute("aria-expanded");
          }
        });
      }
    });
  }

  /**
   * Initialize Dropdowns - Ensure all dropdowns respond to shared state
   */
  function initDropdowns(header) {
    const wrapper = header.querySelector("[data-header-wrapper]");
    if (!wrapper) return;

    // Add mouseenter handlers to all nav items with submenus
    const navItems = header.querySelectorAll("[data-nav-item][data-has-submenu]");
    navItems.forEach((navItem) => {
      navItem.addEventListener("mouseenter", function (e) {
        handleNavItemEnter(e, navItem);
      });

      navItem.addEventListener("mousemove", function (e) {
        handleNavItemEnter(e, navItem);
      });
    });

    // Handle mouse leave on wrapper
    wrapper.addEventListener("mouseleave", function (e) {
      handleHeaderLeave(e);
    });

    // Keep dropdowns and background open when hovering
    const dropdowns = header.querySelectorAll("[data-dropdown]");
    const background = wrapper.querySelector("[data-dropdown-background]");

    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener("mouseenter", function () {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      });

      dropdown.addEventListener("mouseleave", function (e) {
        // Don't close if moving to another nav item or header
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && wrapper.contains(relatedTarget)) {
          return;
        }
        handleHeaderLeave(e);
      });
    });

    // Also keep background open on hover
    if (background) {
      background.addEventListener("mouseenter", function () {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      });

      background.addEventListener("mouseleave", function (e) {
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && wrapper.contains(relatedTarget)) {
          return;
        }
        handleHeaderLeave(e);
      });
    }
  }

  /**
   * Predictive Search - Following Shopify Best Practices
   * Uses section-based approach for predictive search
   */
  function initSearch(header) {
    const searchToggle = header.querySelector("[data-search-toggle]");
    const searchContainer = header.querySelector("[data-search-container]");
    const searchInput = header.querySelector("[data-search-input]");
    const searchResults = header.querySelector("[data-search-results]");
    const searchForm = header.querySelector("[data-search-form]");

    if (!searchToggle || !searchContainer || !searchInput) {
      console.warn("Search elements not found");
      return;
    }

    // Check if predictive search is enabled (from section settings)
    const enablePredictiveSearch = header.dataset.enablePredictiveSearch !== "false";
    const predictiveSearchSectionId = "predictive-search";

    // Toggle search visibility
    function toggleSearch(isOpen) {
      searchContainer.setAttribute("aria-hidden", isOpen ? "false" : "true");
      searchToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");

      if (isOpen) {
        // Close dropdowns when opening search
        const wrapper = header.querySelector("[data-header-wrapper]");
        if (wrapper) {
          dropdownOpen = false;
          wrapper.setAttribute("data-dropdown-open", "false");
        }

        // Focus input
        setTimeout(() => {
          searchInput.focus();
        }, 100);
      } else {
        // Clear results when closing
        if (searchResults) {
          searchResults.innerHTML = "";
        }
      }
    }

    // Toggle search button click handler
    searchToggle.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();

      const isCurrentlyOpen = searchContainer.getAttribute("aria-hidden") === "false";
      toggleSearch(!isCurrentlyOpen);
    });

    // Close search on Escape key
    searchInput.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        const isOpen = searchContainer.getAttribute("aria-hidden") === "false";
        if (isOpen) {
          toggleSearch(false);
        }
      }
    });

    // Close search when clicking outside
    document.addEventListener("click", function (e) {
      if (searchContainer.getAttribute("aria-hidden") === "false") {
        const clickedInside = searchContainer.contains(e.target) || searchToggle.contains(e.target);
        if (!clickedInside) {
          toggleSearch(false);
        }
      }
    });

    // Predictive search using section-based approach (Shopify best practice)
    if (enablePredictiveSearch && searchResults && window.theme && window.theme.routes) {
      const performSearch = debounce(function (query) {
        const trimmedQuery = query.trim();

        if (trimmedQuery.length < 2) {
          searchResults.innerHTML = "";
          searchResults.setAttribute("aria-hidden", "true");
          return;
        }

        // Use section-based predictive search endpoint
        // Format: /search/suggest?q=query&section_id=predictive-search
        const url = new URL("/search/suggest", window.location.origin);
        url.searchParams.set("q", trimmedQuery);
        url.searchParams.set("section_id", predictiveSearchSectionId);
        url.searchParams.set("resources[type]", "product");
        url.searchParams.set("resources[limit]", "4");
        url.searchParams.set("resources[limit_scope]", "each");

        fetch(url)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Search failed: ${response.status}`);
            }
            return response.text();
          })
          .then((text) => {
            // Parse the HTML response and extract the section content
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, "text/html");
            const sectionElement = doc.querySelector(
              `#shopify-section-${predictiveSearchSectionId}`,
            );

            if (sectionElement) {
              searchResults.innerHTML = sectionElement.innerHTML;
              searchResults.setAttribute("aria-hidden", "false");
            } else {
              searchResults.innerHTML = "";
              searchResults.setAttribute("aria-hidden", "true");
            }
          })
          .catch((error) => {
            console.error("Predictive search error:", error);
            searchResults.innerHTML = "";
            searchResults.setAttribute("aria-hidden", "true");
          });
      }, 300);

      // Listen for input changes
      searchInput.addEventListener("input", function () {
        performSearch(this.value);
      });

      // Update ARIA attributes for accessibility
      searchInput.setAttribute("role", "combobox");
      searchInput.setAttribute("aria-expanded", "false");
      searchInput.setAttribute("aria-owns", "predictive-search-results");
      searchInput.setAttribute("aria-controls", "predictive-search-results");
      searchInput.setAttribute("aria-haspopup", "listbox");
      searchInput.setAttribute("aria-autocomplete", "list");
    }

    // Form submission handler
    if (searchForm) {
      searchForm.addEventListener("submit", function (e) {
        const query = searchInput.value.trim();
        if (query.length === 0) {
          e.preventDefault();
          return false;
        }
        // Allow normal form submission to search results page
      });
    }
  }

  /**
   * Keyboard Navigation
   */
  function initKeyboardNavigation(header) {
    // Tab navigation through dropdowns
    const navLinks = header.querySelectorAll("[data-nav-link]");

    navLinks.forEach((link) => {
      link.addEventListener("keydown", function (e) {
        // Enter/Space: Open dropdown on mobile
        if (
          (e.key === "Enter" || e.key === " ") &&
          window.matchMedia("(max-width: 1023px)").matches
        ) {
          e.preventDefault();
          const navItem = link.closest("[data-nav-item]");
          if (navItem && navItem.hasAttribute("data-has-submenu")) {
            handleNavItemEnter(e, navItem);
          }
        }

        // Arrow down: Open dropdown
        if (e.key === "ArrowDown") {
          e.preventDefault();
          const navItem = link.closest("[data-nav-item]");
          if (navItem && navItem.hasAttribute("data-has-submenu")) {
            handleNavItemEnter(e, navItem);
            const dropdown = navItem.querySelector("[data-dropdown]");
            if (dropdown) {
              const firstLink = dropdown.querySelector("a");
              if (firstLink) {
                firstLink.focus();
              }
            }
          }
        }
      });
    });

    // Escape: Close dropdowns
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        const wrapper = header.querySelector("[data-header-wrapper]");
        if (wrapper) {
          dropdownOpen = false;
          wrapper.setAttribute("data-dropdown-open", "false");
        }
      }
    });
  }
})();
